// aidasapps/frontend/src/lib/chartOptionsBuilder.js

// Import Centralized Defaults (for theming/branding)
import { GLOBAL_DEFAULTS, CHART_DEFAULTS, COLOR_PALETTES, WIDGET_DEFAULTS } from './chartDefaults';
import { choroplethRegionFeatureName, inferRegisteredMapId } from './choroplethMapUtils';
import { usaFeatureCenterLngLat } from './geoScatterCentroids';

/** ECharts clamps cumulative roam zoom against `series.map.scaleLimit` (factor vs. initial scale). */
function resolveMapSeriesScaleLimit(mergedMapStyle) {
    const base =
        CHART_DEFAULTS?.map?.scaleLimit && typeof CHART_DEFAULTS.map.scaleLimit === 'object'
            ? CHART_DEFAULTS.map.scaleLimit
            : { min: 0.82, max: 3.25 };
    const raw =
        mergedMapStyle &&
        mergedMapStyle.scaleLimit &&
        typeof mergedMapStyle.scaleLimit === 'object'
            ? mergedMapStyle.scaleLimit
            : null;
    if (!raw) return { ...base };
    let min = raw.min != null ? Number(raw.min) : base.min;
    let max = raw.max != null ? Number(raw.max) : base.max;
    if (!Number.isFinite(min)) min = base.min;
    if (!Number.isFinite(max)) max = base.max;
    if (min > max) {
        const t = min;
        min = max;
        max = t;
    }
    return { min, max };
}

// --- Helper: Format Values ---
// Exported so ChartRenderer can use it for KPI/Table formatting
export const formatValue = (val, config) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number' && Number.isNaN(val)) return '-';
    const num = Number(val);
    if (isNaN(num)) return val;

    let type = 'raw';
    let decimals = 2;
    let symbol = '$';

    if (typeof config === 'string') {
        type = config;
    } else if (config) {
        type = config.type || 'raw';
        if (config.decimals !== undefined) decimals = Number(config.decimals);
        if (config.symbol) symbol = config.symbol;
    }

    try {
        switch (type) {
            case 'number':
            case 'decimal':
                if (config.compact) return Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: decimals }).format(num);
                return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
            case 'integer':
                return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
            case 'float': return num.toFixed(decimals);
            case 'compact':
            case 'abbreviated':
                return Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: decimals }).format(num);
            case 'currency':
                if (config.compact) return `${symbol}${Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: decimals }).format(num)}`;
                return `${symbol}${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
            case 'currency_compact': return `${symbol}${Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: decimals }).format(num)}`;
            case 'percent':
                // Fix: Multiply by 100 for percentage
                return `${(num * 100).toFixed(decimals)}%`;
            default: return val;
        }
    } catch (e) {
        return val;
    }
};

/** Linear interpolate between two #RGB / #RRGGBB colors (t in [0,1]). */
const lerpHexColor = (hexA, hexB, t) => {
    const norm = (h) => {
        if (!h || typeof h !== 'string') return null;
        let s = h.trim().replace('#', '');
        if (s.length === 3) s = s.split('').map((c) => c + c).join('');
        const n = parseInt(s, 16);
        return Number.isFinite(n) ? n : null;
    };
    const ah = norm(hexA);
    const bh = norm(hexB);
    if (ah === null || bh === null) return hexA || '#3b82f6';
    const clampT = Math.max(0, Math.min(1, t));
    const ar = (ah >> 16) & 255;
    const ag = (ah >> 8) & 255;
    const ab = ah & 255;
    const br = (bh >> 16) & 255;
    const bg = (bh >> 8) & 255;
    const bb = bh & 255;
    const r = Math.round(ar + (br - ar) * clampT);
    const g = Math.round(ag + (bg - ag) * clampT);
    const b = Math.round(ab + (bb - ab) * clampT);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// --- Helper: Date Formatting ---
const formatDate = (val, format, separator = '-', pattern = '') => { // keep separator for backward compat signal, though unused in custom
    if (!val) return val;
    // 1. Parse Date
    const d = new Date(val);
    if (isNaN(d.getTime())) return val; // Return original if invalid

    // Helper pads
    const pad = (n) => n.toString().padStart(2, '0');

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const FULL_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    // 2. Custom Pattern Logic (Intelligent)
    if (format === 'custom' && pattern) {
        let out = pattern;

        // Extract basic parts
        const YYYY = d.getFullYear();
        const yy = YYYY.toString().substring(2);
        const M = d.getMonth() + 1;
        const MM = pad(M);
        const D = d.getDate();
        const DD = pad(D);
        const H = d.getHours();
        const HH = pad(H);
        const m = d.getMinutes();
        const mm = pad(m);
        const s = d.getSeconds();
        const ss = pad(s);

        const MMM = MONTH_NAMES[d.getMonth()];
        const MMMM = FULL_MONTH_NAMES[d.getMonth()];

        // --- Intelligent Replacement ---

        // 1. TIME TOKENS (case-insensitive for convenience mostly)
        // Replace 'hh' and 'ss' first to avoid confusion with month/day. 
        out = out.replace(/hh/gi, HH).replace(/ss/gi, ss);

        // 2. YEAR
        out = out.replace(/yyyy/gi, YYYY).replace(/yy/gi, yy);

        // 3. MONTH NAMES (Long & Short) - distinct tokens
        out = out.replace(/mmmm/gi, MMMM);
        out = out.replace(/mmm/gi, MMM);

        // 4. MONTH vs MINUTE (The 'mm' problem)
        // Intelligent Rule: 'mm' is Minutes if preceded by ':' or time-like context.
        // Otherwise 'mm' is Month (01-12).
        // Strategy: Regex with callback to check preceding character.
        out = out.replace(/mm/gi, (match, offset, string) => {
            // Lookbehind simulation: check char at offset-1
            if (offset > 0) {
                const prev = string[offset - 1];
                if (prev === ':' || prev === '.') return mm; // Time separator -> Minute
            }
            // Also check lookahead? "14:mm" vs "dd-mm-yyyy"
            return MM; // Default to Month
        });

        // 5. DAY
        out = out.replace(/dd/gi, DD).replace(/d/gi, D);

        return out;
    }

    // 3. Standard Formats (Legacy Fallback)
    switch (format) {
        case 'yyyy-mm-dd': return `${d.getFullYear()}${separator}${pad(d.getMonth() + 1)}${separator}${pad(d.getDate())}`;
        case 'dd-mm-yyyy': return `${pad(d.getDate())}${separator}${pad(d.getMonth() + 1)}${separator}${d.getFullYear()}`;
        case 'mm-dd-yyyy': return `${pad(d.getMonth() + 1)}${separator}${pad(d.getDate())}${separator}${d.getFullYear()}`;
        case 'mmm-yyyy': return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`; // Warning: this block needs MONTH_NAMES in scope if accessed here. 
        // (fixed by moving it inside switch or global const)
        // Redefined minimal here for safety if fallthrough
        case 'quarter':
            const q = Math.floor((d.getMonth() + 3) / 3);
            return `Q${q} ${d.getFullYear()}`;
        default: return val;
    }
};


// --- Helper: Font Styles ---
const mapFontStyle = (config, legacyKey = 'labelFont') => {
    // 1. Try Legacy Nested Config
    const legacy = config?.[legacyKey];
    if (legacy) {
        return {
            color: legacy.color || '#333',
            fontSize: legacy.size || 12,
            fontWeight: legacy.bold ? 'bold' : 'normal',
            fontStyle: legacy.italic ? 'italic' : 'normal',
            textDecoration: legacy.underline ? 'underline' : 'none'
        };
    }

    // 2. Try Flat Config (New FormatConfigView)
    if (config) {
        return {
            color: config.color || '#333',
            fontSize: config.fontSize || (legacyKey === 'titleFont' ? 14 : 12),
            fontWeight: config.fontWeight || 'normal',
            fontStyle: config.fontStyle || 'normal'
        };
    }

    return { fontSize: 12 };
};

// --- Helper: Legend/Title Position ---
const mapPosition = (style) => {
    const vPos = style.legend?.vPosition || GLOBAL_DEFAULTS.legend.vPosition;
    const hPos = style.legend?.hPosition || GLOBAL_DEFAULTS.legend.hPosition;

    // Custom Position
    if (hPos === 'custom') {
        const top = style.legend?.customTop ?? 0;
        const left = style.legend?.customLeft ?? 0;
        return { top: `${top}%`, left: `${left}%` };
    }

    const result = {};
    if (vPos === 'top') result.top = 5;
    else if (vPos === 'bottom') result.bottom = 5;
    else result.top = 'middle';

    if (hPos === 'left') result.left = 10;
    else if (hPos === 'right') result.right = 10;
    else result.left = 'center';

    return result;
};

// --- Helper: Stacked 100% Normalizer ---
const normalizeTo100Percent = (data, ySeries) => {
    // 1. Calculate totals per row (x-axis index)
    const rowTotals = data.map(row => {
        let sum = 0;
        ySeries.forEach(s => {
            sum += (Number(row[s.id]) || 0);
        });
        return sum;
    });

    // 2. Normalize values
    return data.map((row, idx) => {
        const newRow = { ...row };
        const total = rowTotals[idx];
        ySeries.forEach(s => {
            const original = Number(row[s.id]) || 0;
            // Avoid divide by zero
            newRow[s.id] = total === 0 ? 0 : (original / total) * 100;
        });
        return newRow;
    });
};

// --- MAIN CHART BUILDER ---
export const buildChartOptions = (type, data, config) => {
    // Skip types handled by SimpleDisplay
    if (['text', 'kpi', 'table'].includes(type)) return null;
    if (!data || !data.length) return null;

    const { data: dataCfg, style = {} } = config;
    const xData = data.map(r => r.x_val);

    // --- 1. PREPARE FORMATTERS ---
    const globalLabelFormat = style.dataLabelFormat || { type: 'raw' };
    const tooltipFormat = style.tooltip?.valueFormat || { type: 'raw' };
    const dateFormat = dataCfg.dateFormat || 'raw';
    const dateSep = dataCfg.dateSeparator || '-';
    const datePat = dataCfg.datePattern || '';

    // --- 2. COMMON SETUP (Grid, Tooltip, Legend) ---
    const isCartesian = ['bar', 'line', 'area', 'scatter', 'boxplot', 'combo', 'bubble', 'sparkline', 'waterfall'].includes(type);

    // Grid (Padding) - Using centralized defaults
    let grid = {
        containLabel: true,
        left: GLOBAL_DEFAULTS.plotArea.paddingLeft,
        right: GLOBAL_DEFAULTS.plotArea.paddingRight,
        top: GLOBAL_DEFAULTS.plotArea.paddingTop,
        bottom: GLOBAL_DEFAULTS.plotArea.paddingBottom
    };

    const hasSecondaryAxis = type === 'combo' && dataCfg.ySeries?.some(s => s.axis === 'right');

    if (style.plotArea) {
        // Respect manual overrides or use default
        if (style.plotArea.paddingLeft !== undefined) grid.left = style.plotArea.paddingLeft;
        if (style.plotArea.paddingRight !== undefined) grid.right = style.plotArea.paddingRight;
        if (style.plotArea.paddingTop !== undefined) grid.top = style.plotArea.paddingTop;
        if (style.plotArea.paddingBottom !== undefined) grid.bottom = style.plotArea.paddingBottom;

        // Auto-Padding for Axis Titles (if not manually overridden)
        if (style.xAxis?.showTitle && style.plotArea.paddingBottom === undefined) grid.bottom += 20;
        if (style.yAxis?.showTitle && style.plotArea.paddingLeft === undefined) grid.left += 20;
        if (hasSecondaryAxis && style.secondaryYAxis?.showTitle && style.plotArea.paddingRight === undefined) grid.right += 20;
    } else {
        // Fallback Auto-Padding if no plotArea config exists at all
        if (style.xAxis?.showTitle) grid.bottom += 20;
        if (style.yAxis?.showTitle) grid.left += 20;
        if (hasSecondaryAxis && style.secondaryYAxis?.showTitle) grid.right += 20;
    }
    // Sparkline Special Grid
    if (type === 'sparkline') grid = { left: 0, right: 0, top: 0, bottom: 0, containLabel: false };

    // Tooltip
    // Re-use robust formatter logic from before (inlined for clarity/scope access)
    const getTooltipFormatter = (trigger) => {
        // Choropleth: regions without mapped data expose NaN/undefined via ECharts; avoid showing "NaN".
        if (type === 'choropleth' || type === 'map') {
            return (params) => {
                const raw = Array.isArray(params) ? params[0] : params;
                if (!raw) return '';
                const regionName = raw.name != null ? String(raw.name) : '';
                let v = raw.value;
                if (Array.isArray(v)) {
                    // Geo bubble map: value is [lng, lat, metric]
                    if (v.length >= 3) v = v[2];
                    else if (v.length >= 1) v = v[0];
                } else if (v && typeof v === 'object' && !Array.isArray(v) && v.value !== undefined) {
                    v = v.value;
                }
                const num = Number(v);
                const display =
                    Number.isFinite(num)
                        ? formatValue(num, tooltipFormat)
                        : '-';
                return `<div style="font-size:11px;line-height:1.45">${regionName}<div style="font-weight:600;margin-top:4px">${display}</div></div>`;
            };
        }
        if (type === 'treemap') {
            return (params) => {
                const raw = Array.isArray(params) ? params[0] : params;
                if (!raw) return '';
                const nodeName = raw.name != null ? String(raw.name) : '';
                const sizeSeries =
                    dataCfg.ySeries?.find((s) => s.role === 'yAxis' || !s.role) ||
                    dataCfg.ySeries?.[0];
                const colorSeries = dataCfg.ySeries?.find((s) => s.role === 'treemapColor');
                let v = raw.value;
                if (v && typeof v === 'object' && !Array.isArray(v) && v.value !== undefined) v = v.value;
                if (Array.isArray(v)) v = v[0];
                const num = Number(v);
                const sizeLabel = sizeSeries?.name || sizeSeries?.id || 'Value';
                let sizeFmt = tooltipFormat;
                if (sizeSeries?.id && style.seriesFormats?.[sizeSeries.id]) {
                    sizeFmt = style.seriesFormats[sizeSeries.id];
                }
                const sizeDisplay = Number.isFinite(num) ? formatValue(num, sizeFmt) : '-';
                let colorLine = '';
                if (colorSeries?.id && raw.data && Object.prototype.hasOwnProperty.call(raw.data, colorSeries.id)) {
                    const cv = Number(raw.data[colorSeries.id]);
                    if (Number.isFinite(cv)) {
                        let cFmt = tooltipFormat;
                        if (style.seriesFormats?.[colorSeries.id]) {
                            cFmt = style.seriesFormats[colorSeries.id];
                        }
                        colorLine = `<div style="margin-top:4px">${colorSeries.name || colorSeries.id}: <b>${formatValue(cv, cFmt)}</b></div>`;
                    }
                }
                return `<div style="font-size:11px;line-height:1.45"><div style="font-weight:700">${nodeName}</div><div style="margin-top:4px">${sizeLabel}: <b>${sizeDisplay}</b></div>${colorLine}</div>`;
            };
        }
        return (params) => {
            const pList = Array.isArray(params) ? params : [params];
            if (!pList[0]) return '';
            const pFirst = pList[0];

            // 1. Header (Name)
            const rawName = pFirst.name;
            const name = (dateFormat !== 'raw') ? formatDate(rawName, dateFormat, dateSep, datePat) : rawName;

            // 2. Extras (Attached to first point)
            let extrasHtml = '';
            if (pFirst.data?.extras) {
                extrasHtml = `<div style="margin-top:8px;padding-top:4px;border-top:1px solid #555;font-size:10px;color:#ddd;">`;
                Object.entries(pFirst.data.extras).forEach(([key, val]) => {
                    const fmt = style.seriesFormats?.[key];
                    const displayVal = fmt ? formatValue(val, fmt) : val;
                    extrasHtml += `<div style="display:flex;justify-content:space-between;margin-bottom:2px;">
                        <span style="opacity:0.8;margin-right:10px;">${key}:</span> 
                        <span style="font-weight:bold;">${displayVal}</span>
                    </div>`;
                });
                extrasHtml += `</div>`;
            }

            // 3. Special Types
            if (type === 'boxplot') { /* ... boxplot logic ... */ }

            // 4. Body
            let html = '';
            if (trigger === 'axis' || pList.length > 1) {
                html += `<div style="font-size:11px;font-weight:bold;margin-bottom:4px;">${name}</div>`;
            }

            pList.forEach(item => {
                const label = (type === 'pie' || type === 'donut') ? item.name : item.seriesName;

                // Formatter Resolution
                let useFormat = tooltipFormat;
                const seriesConfig = dataCfg.ySeries?.find(s => (s.name || s.field) === item.seriesName);
                if (seriesConfig && style.seriesFormats?.[seriesConfig.id]) {
                    useFormat = style.seriesFormats[seriesConfig.id];
                }

                // Unwrap
                let val = item.value;
                if (typeof val === 'object' && val?.value !== undefined) val = val.value; // bubble/heatmap often array, handled by ECharts formatter usually but explicitly:
                if (Array.isArray(val)) val = val[val.length - 1]; // bubble: last is value usually? No, scatter is [x, y, size]. value is [2]? 
                // Actually for cartesian, item.value is usually the Y value or [x, y]. Echarts logic varies. 
                // Simplification: ECharts params.value is often usable directly for formatting if simple.
                // If it's an object {value, extras}, ECharts passes {value: {value:..., extras:...}} to item.data
                // But params.value is effectively the raw number usually for bar/line.

                // Robust Unwrap for "extras" pattern
                if (typeof item.data === 'object' && item.data?.value !== undefined) {
                    val = item.data.value; // Ensure we format the actual number, not the object
                }

                const displayVal = formatValue(val, useFormat);

                let suffix = item.percent ? ` <span style="opacity:0.7">(${item.percent}%)</span>` : '';

                if (trigger === 'item' && pList.length === 1 && !isCartesian) {
                    html = `${item.marker} ${label}: <b>${displayVal}</b>${suffix}`;
                    return;
                }

                html += `<div style="display:flex;justify-content:space-between;gap:15px;font-size:11px;align-items:center;">
                    <span>${item.marker} ${label}</span>
                    <span style="font-weight:bold;">${displayVal}${suffix}</span>
                </div>`;
            });

            return html + extrasHtml;
        };
    };

    const trigger = style.tooltip?.trigger || (isCartesian ? 'axis' : 'item');

    const option = {
        backgroundColor: 'transparent', // Grid handles background
        animation: config.interactions?.animation !== false,
        // Top-level color palette — ECharts cycles through this for all series automatically.
        // Populated when user picks a palette or per-series color in the Format panel.
        ...(style.colors && style.colors.length > 0 ? { color: style.colors } : {}),
        tooltip: {
            show: style.tooltip?.show !== false,
            trigger: trigger,
            backgroundColor: style.tooltip?.backgroundColor || GLOBAL_DEFAULTS.tooltip.backgroundColor,
            borderColor: style.tooltip?.borderColor || GLOBAL_DEFAULTS.tooltip.borderColor,
            borderWidth: style.tooltip?.borderColor ? 1 : 0,
            textStyle: {
                ...mapFontStyle(style.tooltip, 'font'),
                color: style.tooltip?.color || GLOBAL_DEFAULTS.tooltip.textColor
            },
            confine: true,
            formatter: getTooltipFormatter(trigger)
        },
        legend: {
            show: style.legend?.show !== false && !['sparkline', 'map', 'heatmap', 'treemap', 'gauge'].includes(type),
            type: 'scroll',
            orient: (style.legend?.vPosition === 'middle') ? 'vertical' : 'horizontal',
            ...mapPosition(style),
            pageIconSize: 8,
            pageIconColor: '#64748b',
            pageIconInactiveColor: '#cbd5e1',
            pageTextStyle: { fontSize: 9, color: '#64748b' },
            textStyle: {
                ...mapFontStyle(style.legend, 'font'),
                color: style.legend?.fontColor || GLOBAL_DEFAULTS.legend.fontColor
            }
        },
        grid: {
            ...grid,
            show: !!(style.plotArea?.backgroundColor || style.plotArea?.showBorder),
            backgroundColor: style.plotArea?.backgroundColor || 'transparent',
            borderColor: style.plotArea?.borderColor || 'transparent',
            borderWidth: style.plotArea?.showBorder ? 1 : 0
        }
    };

    // --- 2.1 DATA ZOOM (Cartesian charts) ---
    if (style.dataZoom && isCartesian && type !== 'sparkline') {
        option.dataZoom = [
            {
                type: 'slider',
                show: true,
                xAxisIndex: [0],
                bottom: 10,
                height: 20,
                borderColor: 'transparent',
                backgroundColor: 'rgba(239, 246, 255, 0.5)', // light blue tint
                fillerColor: 'rgba(59, 130, 246, 0.2)', // blue-500 @ 20%
                handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                handleSize: '80%',
                handleStyle: {
                    color: '#fff',
                    shadowBlur: 3,
                    shadowColor: 'rgba(0, 0, 0, 0.6)',
                    shadowOffsetX: 2,
                    shadowOffsetY: 2
                },
                textStyle: { color: '#64748b' }
            },
            {
                type: 'inside',
                xAxisIndex: [0]
            }
        ];
        // Ensure grid has enough room at the bottom for the slider
        if (option.grid.bottom < 40) {
            option.grid.bottom = 40;
        }
    }

    // Data Labels Common Config
    const dataLabelCfg = {
        show: style.showDataLabel,
        position: style.dataLabel?.position || (type === 'bar' ? 'top' : 'outside'),
        rotate: style.dataLabel?.rotate || 0,
        ...mapFontStyle(style.dataLabel, 'font'),
        formatter: (p) => {
            // 1. Inherit/Fallback: Try Series Format
            if (p.seriesName) {
                const sConfig = dataCfg.ySeries?.find(s => (s.name || s.field) === p.seriesName);
                if (sConfig && style.seriesFormats?.[sConfig.id]) {
                    return formatValue(p.value, style.seriesFormats[sConfig.id]);
                }
            }
            // 2. Default (Compact usually for axis, but maybe raw for label? Let's use compact for consistency if axis is compact)
            return formatValue(p.value, 'compact');
        }
    };

    // --- 3. AXES & SERIES (CARTESIAN - CATEGORY/VALUE) ---
    // Standard Bar/Line/Area/Combo (Category on one axis, Value on other)
    if (['bar', 'line', 'area', 'combo', 'sparkline', 'boxplot', 'waterfall'].includes(type)) {

        let isHorizontal = (type === 'bar' && style.bar?.horizontal) || (type === 'waterfall' && style.waterfall?.horizontal) || (type === 'boxplot' && style.boxplot?.horizontal);
        // Sparkline forces specific axis
        if (type === 'sparkline') isHorizontal = false;

        const isStacked100 = (type === 'bar' && style.bar?.stacked && style.bar?.stackType === '100%') ||
            (['line', 'area'].includes(type) && style.line?.stacked && style.line?.stackType === '100%');
        let finalData = data;

        // Stacked 100% Normalization
        if (isStacked100) {
            finalData = normalizeTo100Percent(data, dataCfg.ySeries);
        }

        // Axes Setup
        const createValueAxis = (axisStyle = {}, isSecondary = false) => ({
            type: axisStyle.logScale ? 'log' : 'value',
            show: type !== 'sparkline' && axisStyle.show !== false,
            max: isStacked100 ? 100 : undefined,
            name: (axisStyle.showTitle === true) ? axisStyle.titleText : undefined,
            nameLocation: 'middle',
            nameGap: 30,
            position: isSecondary ? 'right' : 'left',
            nameTextStyle: {
                color: axisStyle.labelColor || (isSecondary ? '#6366f1' : GLOBAL_DEFAULTS.yAxis.labelColor),
                fontStyle: 'italic'
            },
            axisLabel: {
                ...mapFontStyle(axisStyle, 'labelFont'),
                color: axisStyle.labelColor || (isSecondary ? '#6366f1' : GLOBAL_DEFAULTS.yAxis.labelColor),
                formatter: (val) => {
                    // Try to find if single series to apply specific format
                    if (dataCfg.ySeries?.length === 1 && !isSecondary) {
                        const sid = dataCfg.ySeries[0].id;
                        if (style.seriesFormats?.[sid]) return formatValue(val, style.seriesFormats[sid]);
                    }
                    return formatValue(val, { type: 'compact' });
                }
            },
            axisLine: { show: true, lineStyle: { color: axisStyle.lineColor || (isSecondary ? '#6366f1' : GLOBAL_DEFAULTS.yAxis.lineColor) } },
            splitLine: {
                show: isSecondary ? (axisStyle.showGrid === true) : (axisStyle.showGrid !== false),
                lineStyle: { color: axisStyle.gridColor || GLOBAL_DEFAULTS.yAxis.gridColor }
            }
        });

        const primaryAxis = createValueAxis(style.yAxis);
        const yAxes = [primaryAxis];
        if (hasSecondaryAxis) {
            yAxes.push(createValueAxis(style.secondaryYAxis || {}, true));
        }

        const categoryAxis = {
            type: 'category',
            data: xData,
            inverse: isHorizontal, // FIX: Render Top-to-Bottom for Horizontal Bars (Index 0 at Top)
            show: type !== 'sparkline' && style.xAxis?.show !== false,
            name: (style.xAxis?.showTitle === true) ? style.xAxis?.titleText : undefined,
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                color: style.xAxis?.labelColor || GLOBAL_DEFAULTS.xAxis.labelColor,
                fontStyle: 'italic'
            },
            axisLabel: {
                rotate: style.xAxis?.labelRotation || 0,
                ...mapFontStyle(style.xAxis, 'labelFont'),
                color: style.xAxis?.labelColor || GLOBAL_DEFAULTS.xAxis.labelColor,
                formatter: (val) => (dateFormat !== 'raw') ? formatDate(val, dateFormat, dateSep, datePat) : val
            },
            axisLine: { show: true, lineStyle: { color: style.xAxis?.lineColor || GLOBAL_DEFAULTS.xAxis.lineColor } },
            splitLine: { show: style.xAxis?.showGrid !== false, lineStyle: { color: style.xAxis?.gridColor || GLOBAL_DEFAULTS.xAxis.gridColor } }
        };

        if (isHorizontal) {
            option.xAxis = primaryAxis; // Values on Top/Horizontal
            option.yAxis = categoryAxis;
        } else {
            option.xAxis = categoryAxis;
            option.yAxis = yAxes;
        }

        // Series Generation (Skip for Waterfall and Boxplot - handled separately)
        if (type !== 'waterfall' && type !== 'boxplot') {
            option.series = dataCfg.ySeries.map((s, idx) => {
                const seriesType = (type === 'combo') ? (s.type || 'bar') : (type === 'area' ? 'line' : type); // Default to bar if combo missing type
                const yAxisIndex = s.axis === 'right' ? 1 : 0;

                // Per-Series Settings (Horizontal/Vertical separation mainly for Bar)
                // But specific props like width/smooth depend on seriesType

                const seriesDisplayName = style.seriesFormats?.[s.id]?.displayName || s.name || s.field;
                const baseSeries = {
                    name: seriesDisplayName,
                    type: seriesType,
                    data: finalData.map((row, rIdx) => {
                        const val = row[s.id];
                        let item = { value: val };
                        // Attach Extras
                        if (dataCfg.tooltipFields) {
                            item.extras = {};
                            dataCfg.tooltipFields.forEach(f => item.extras[f] = row[f]);
                        }
                        if (isStacked100) item.originalValue = data[rIdx][s.id];
                        return item;
                    }),
                    itemStyle: {
                        color: style.colors?.[idx] || s.color,
                        borderRadius: (seriesType === 'bar' && style.bar?.borderRadius) ? style.bar.borderRadius : 0
                    },
                    yAxisIndex: yAxisIndex
                };

                // Bar Specifics
                if (seriesType === 'bar') {
                    baseSeries.barWidth = style.bar?.barWidth === 'auto' ? undefined : style.bar?.barWidth;
                    if (style.bar?.stacked) {
                        baseSeries.stack = 'total';
                    }
                }

                // Line/Area Specifics
                if (seriesType === 'line' || seriesType === 'area') {
                    baseSeries.smooth = style.line?.smooth === 'step' ? false : (style.line?.smooth ?? false);
                    baseSeries.step = style.line?.smooth === 'step' ? 'start' : undefined;
                    baseSeries.showSymbol = style.line?.showSymbol !== false;
                    baseSeries.symbolSize = style.line?.symbolSize ?? 4;

                    // Stacking (New)
                    if (style.line?.stacked) {
                        baseSeries.stack = 'total';
                    }

                    if (type === 'area' || style.line?.area) {
                        baseSeries.areaStyle = { opacity: style.area?.opacity ?? 0.3 };
                    }

                    // Forecast & Confidence Styling
                    if (s.role === 'forecast') {
                        baseSeries.lineStyle = { type: 'dotted', width: 3 };
                    }
                    if (s.role === 'confidence') {
                        baseSeries.type = 'line'; // Force line for confidence bounds
                        baseSeries.symbol = 'none';
                        baseSeries.lineStyle = { type: 'dashed', width: 1, opacity: 0.6 };
                        // Optional: If we wanted a band, we'd need complex stacking. 
                        // Dashed lines are a safe, clear default.
                    }
                }

                // Bubble Specifics (Series Type often 'scatter' in ECharts)
                if (type === 'bubble') {
                    // Bubble needs [x, y, size] usually? 
                    // Currently generic 'scatter' handles 2D. 
                    // ECharts Bubble: visualMap handles size mapping OR symbolSize callback.
                    baseSeries.type = 'scatter';
                    // Calculate scale based on value?
                    const maxVal = Math.max(...finalData.map(r => Number(r[s.id]) || 0));
                    const maxSize = style.bubble?.maxSize || 50;
                    baseSeries.symbolSize = (val) => {
                        // val is [x, y] or scalar?
                        // In axis chart, val is Y value.
                        // Scale: (val / max) * maxSize
                        // Avoid divide zero
                        const v = Array.isArray(val) ? val[val.length - 1] : val;
                        return (Number(v) / maxVal) * maxSize;
                    };
                }

                // Sparkline Specifics
                if (type === 'sparkline') {
                    baseSeries.type = 'line';
                    baseSeries.showSymbol = false;
                    baseSeries.areaStyle = { opacity: 0.1 };
                    baseSeries.lineStyle = { width: 1 };
                }

                return { ...baseSeries, label: dataLabelCfg };
            });
        } // End of if (type !== 'waterfall')
    }

    // WATERFALL (Series only - Axes handled in Cartesian block above)
    if (type === 'waterfall') {
        const seriesId = dataCfg.ySeries?.[0]?.id;
        if (seriesId) {
            const helperData = [];
            const valueData = [];
            const categoryLabels = [];

            // Detection of Key Mismatch (e.g. Aggregation Aliases)
            const firstRow = data[0] || {};
            let actualSeriesKey = seriesId;
            if (firstRow[seriesId] === undefined) {
                console.warn('[Waterfall] Series ID not found in row. Attempting auto-detect.', { seriesId, keys: Object.keys(firstRow) });
                const keys = Object.keys(firstRow);
                // 1. Case Insensitive
                let match = keys.find(k => k.toLowerCase() === seriesId.toLowerCase());
                // 2. Partial Match (e.g. sum_ChargeAmount vs ChargeAmount)
                if (!match) match = keys.find(k => k.toLowerCase().includes(seriesId.toLowerCase()));

                if (match) {
                    actualSeriesKey = match;
                    // console.log(`[Waterfall] Using detected key: ${actualSeriesKey}`);
                }
            }

            // Waterfall options
            const isAscending = style.waterfall?.ascending === true; // Reversed (water going up)
            const showTotal = style.waterfall?.showTotal !== false;   // Default: true

            // Calculate cumulative values based on mode
            if (isAscending) {
                // ASCENDING MODE: Items build up to total at the end
                // Process data in order, each bar adds to the cumulative
                let cumulative = 0;

                data.forEach((row, idx) => {
                    const rawVal = Number(row[actualSeriesKey]) || 0;
                    const stepType = row['step_type']?.toLowerCase() || '';
                    const label = row.x_val || `Step ${idx + 1}`;

                    // Extras for tooltip
                    const extras = {};
                    if (dataCfg.tooltipFields) dataCfg.tooltipFields.forEach(f => extras[f] = row[f]);

                    // Skip if data explicitly marks as start/end (for manual control)
                    const isStart = stepType === 'start';
                    const isEnd = stepType === 'end';

                    if (isStart) {
                        // Starting point (base value)
                        helperData.push(0);
                        cumulative = rawVal;
                        valueData.push({
                            value: rawVal,
                            extras: extras,
                            itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                        });
                        categoryLabels.push(label);
                    } else if (isEnd) {
                        // End/Total marker in data
                        helperData.push(0);
                        valueData.push({
                            value: cumulative,
                            extras: { ...extras, originalValue: cumulative },
                            itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                        });
                        categoryLabels.push(label);
                    } else {
                        // Regular data point
                        if (rawVal >= 0) {
                            helperData.push(cumulative);
                            cumulative += rawVal;
                            valueData.push({
                                value: rawVal,
                                extras: extras,
                                itemStyle: { color: style.waterfall?.increaseColor || CHART_DEFAULTS.waterfall.increaseColor, borderRadius: 2 }
                            });
                        } else {
                            cumulative += rawVal;
                            helperData.push(cumulative);
                            valueData.push({
                                value: Math.abs(rawVal),
                                extras: { ...extras, originalValue: rawVal },
                                itemStyle: { color: style.waterfall?.decreaseColor || '#ef4444', borderRadius: 2 }
                            });
                        }
                        categoryLabels.push(label);
                    }
                });

                // Add TOTAL bar at the END (ascending mode)
                if (showTotal && !data.some(r => r['step_type']?.toLowerCase() === 'end')) {
                    helperData.push(0);
                    valueData.push({
                        value: cumulative,
                        extras: { originalValue: cumulative },
                        itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                    });
                    categoryLabels.push('Total');
                }

            } else {
                // DESCENDING MODE (Default): Total at START, then breakdown
                // First, calculate the grand total
                const grandTotal = data.reduce((sum, row) => sum + (Number(row[actualSeriesKey]) || 0), 0);
                let cumulative = grandTotal;
                let addedStartTotal = false;

                // Add TOTAL bar at the START (descending mode)
                if (showTotal && !data.some(r => r['step_type']?.toLowerCase() === 'start')) {
                    helperData.push(0);
                    valueData.push({
                        value: grandTotal,
                        extras: { originalValue: grandTotal },
                        itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                    });
                    categoryLabels.push('Total');
                    addedStartTotal = true;
                }

                data.forEach((row, idx) => {
                    const rawVal = Number(row[actualSeriesKey]) || 0;
                    const stepType = row['step_type']?.toLowerCase() || '';
                    const label = row.x_val || `Step ${idx + 1}`;

                    // Extras for tooltip
                    const extras = {};
                    if (dataCfg.tooltipFields) dataCfg.tooltipFields.forEach(f => extras[f] = row[f]);

                    const isStart = stepType === 'start';
                    const isEnd = stepType === 'end';

                    if (isStart && !addedStartTotal) {
                        // Starting total from data
                        helperData.push(0);
                        cumulative = rawVal;
                        valueData.push({
                            value: rawVal,
                            extras: extras,
                            itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                        });
                        categoryLabels.push(label);
                    } else if (isEnd) {
                        // End marker
                        helperData.push(0);
                        valueData.push({
                            value: cumulative,
                            extras: { ...extras, originalValue: cumulative },
                            itemStyle: { color: style.waterfall?.totalColor || '#5470c6', borderRadius: 2 }
                        });
                        categoryLabels.push(label);
                    } else if (!isStart) {
                        // Regular data point - subtract from cumulative (breakdown mode)
                        cumulative -= rawVal;
                        if (rawVal >= 0) {
                            helperData.push(cumulative);
                            valueData.push({
                                value: rawVal,
                                extras: extras,
                                itemStyle: { color: style.waterfall?.decreaseColor || '#ef4444', borderRadius: 2 } // In descending, positive = reduction
                            });
                        } else {
                            helperData.push(cumulative + Math.abs(rawVal));
                            valueData.push({
                                value: Math.abs(rawVal),
                                extras: { ...extras, originalValue: rawVal },
                                itemStyle: { color: style.waterfall?.increaseColor || '#91cc75', borderRadius: 2 } // Negative = increase
                            });
                        }
                        categoryLabels.push(label);
                    }
                });
            }

            // Update xAxis/yAxis data for waterfall with custom labels
            const isHorizontal = style.waterfall?.horizontal;
            if (isHorizontal) {
                option.yAxis.data = categoryLabels;
            } else {
                option.xAxis.data = categoryLabels;
            }

            option.series = [
                {
                    name: 'Placeholder',
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { borderColor: 'transparent', color: 'transparent' },
                    emphasis: { itemStyle: { borderColor: 'transparent', color: 'transparent' } },
                    data: helperData,
                    tooltip: { show: false },
                    animation: false
                },
                {
                    name: dataCfg.ySeries[0].name || 'Value',
                    type: 'bar',
                    stack: 'total',
                    data: valueData,
                    label: {
                        ...dataLabelCfg,
                        position: style.dataLabel?.position || 'top',
                        formatter: (params) => {
                            const val = params.data?.extras?.originalValue ?? params.value;
                            // Use imported formatValue
                            return formatValue(val, dataCfg.ySeries[0].valueFormat || 'raw');
                        }
                    }
                }
            ];
        }
    }

    // BOXPLOT (Series only - Axes handled in Cartesian block above)
    if (type === 'boxplot') {
        // Get the y-field (metric to analyze distribution)
        // In columns mode, data has raw field names; in aggregated mode, data has series IDs
        const ySeries = dataCfg.ySeries?.[0];
        const seriesId = ySeries?.id;
        const fieldName = ySeries?.name || ySeries?.field;

        // Try to find the column - could be seriesId (aggregated) or fieldName (raw columns)
        const firstRow = data?.[0] || {};
        let actualKey = null;
        if (seriesId && firstRow.hasOwnProperty(seriesId)) {
            actualKey = seriesId;
        } else if (fieldName && firstRow.hasOwnProperty(fieldName)) {
            actualKey = fieldName;
        } else {
            // Try to find any numeric column (fallback)
            const numericKeys = Object.keys(firstRow).filter(k => k !== 'x_val' && typeof firstRow[k] === 'number');
            if (numericKeys.length > 0) actualKey = numericKeys[0];
        }

        // console.log('[Boxplot] Using data key:', actualKey, 'from options:', { seriesId, fieldName });

        if (actualKey) {
            // Group data by x_val (category/dimension)
            const groupedData = {};
            data.forEach(row => {
                const category = row.x_val || 'All';
                if (!groupedData[category]) groupedData[category] = [];
                const val = Number(row[actualKey]);
                if (!isNaN(val)) groupedData[category].push(val);
            });

            // Helper to calculate quantile
            const quantile = (arr, q) => {
                const sorted = [...arr].sort((a, b) => a - b);
                const pos = (sorted.length - 1) * q;
                const base = Math.floor(pos);
                const rest = pos - base;
                if (sorted[base + 1] !== undefined) {
                    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
                }
                return sorted[base];
            };

            // Calculate boxplot statistics for each category
            const categories = Object.keys(groupedData);
            const boxplotData = categories.map(cat => {
                const values = groupedData[cat].sort((a, b) => a - b);
                if (values.length === 0) return [0, 0, 0, 0, 0];

                const min = values[0];
                const max = values[values.length - 1];
                const q1 = quantile(values, 0.25);
                const median = quantile(values, 0.5);
                const q3 = quantile(values, 0.75);

                // Calculate outliers (values outside 1.5*IQR)
                const iqr = q3 - q1;
                const lowerFence = q1 - 1.5 * iqr;
                const upperFence = q3 + 1.5 * iqr;
                const outliers = values.filter(v => v < lowerFence || v > upperFence);

                return {
                    stats: [min, q1, median, q3, max],
                    outliers: outliers,
                    category: cat
                };
            });

            // Update axis data with categories
            const isHorizontal = style.boxplot?.horizontal;
            const targetAxis = isHorizontal ?
                (Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis) :
                (Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis);

            if (targetAxis) {
                targetAxis.data = categories;
                targetAxis.type = 'category'; // Ensure it's category
                targetAxis.boundaryGap = true;
            }

            const boxplotName = style.seriesFormats?.[seriesId]?.displayName || ySeries?.name || ySeries?.field || 'Distribution';

            option.series = [
                {
                    name: boxplotName,
                    type: 'boxplot',
                    layout: isHorizontal ? 'horizontal' : 'vertical',
                    data: boxplotData.map(d => d.stats),
                    itemStyle: {
                        color: style.colors?.[0] || '#5470c6',
                        borderColor: style.colors?.[0] || '#5470c6'
                    },
                    boxWidth: [`${style.boxplot?.boxWidth || 50}%`, `${style.boxplot?.boxWidth || 50}%`]
                }
            ];

            // Add outliers if enabled
            if (style.boxplot?.showOutliers !== false) {
                const outlierData = [];
                boxplotData.forEach((d, idx) => {
                    d.outliers.forEach(val => {
                        // Horizontal: [val, idx], Vertical: [idx, val]
                        outlierData.push(isHorizontal ? [val, idx] : [idx, val]);
                    });
                });

                if (outlierData.length > 0) {
                    option.series.push({
                        name: 'Outliers',
                        type: 'scatter',
                        data: outlierData,
                        itemStyle: {
                            color: style.boxplot?.outlierColor || style.colors?.[0] || '#5470c6' // Configured outlier color OR match the boxplot
                        },
                        symbolSize: 6
                    });
                }
            }
        }
    }

    // --- 4. PIE / DONUT ---
    else if (['pie', 'donut'].includes(type)) {
        const s = dataCfg.ySeries[0];
        if (s) {
            // "Half-Doughnut" Logic
            const isHalf = style.pie?.isHalf;
            // Shift pie center based on legend position to avoid overlap
            const legendHPos = style.legend?.hPosition || GLOBAL_DEFAULTS.legend.hPosition;
            const legendShow = style.legend?.show !== false;
            let pieCenterX = '50%';
            if (legendShow) {
                if (legendHPos === 'right') pieCenterX = '38%';
                else if (legendHPos === 'left') pieCenterX = '60%';
            }
            const center = isHalf ? [pieCenterX, '70%'] : [pieCenterX, '50%'];
            const startAngle = isHalf ? 180 : 90;
            const endAngle = isHalf ? 360 : undefined; // ECharts doesn't explicitly limit end, but 180 start implies half if 360 total? 
            // Wait, ECharts uses startAngle. Half donut is usually start 180, end 360? No, ECharts wraps.
            // Half Donut: start 180. Data must sum to 50%? No.
            // Correct way: Center at bottom, startAngle 180.

            option.series = [{
                type: 'pie',
                top: grid.top,
                bottom: grid.bottom,
                left: grid.left,
                right: grid.right,
                radius: (type === 'donut')
                    ? [`${style.pie?.innerRadius ?? 40}%`, '70%']
                    : [0, `${style.pie?.outerRadius ?? 70}%`], // Use config or default
                center: center,
                startAngle: startAngle,
                // endAngle isn't a direct prop on Pie, but we can simulate via data or gauge usually. 
                // Actually for "Half Donut" visualization of parts-of-whole, usually just positioning is enough?
                // If it's a Gauge-style half donut, we need type='gauge'. 
                // Let's stick to standard Pie with center adjustment for now per snippet.

                roseType: style.pie?.roseType || false,
                itemStyle: {
                    borderRadius: style.pie?.borderRadius ?? 0,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                padAngle: style.pie?.padAngle ?? 0,

                label: {
                    ...dataLabelCfg,
                    formatter: (p) => `${p.name}: ${formatValue(p.value, globalLabelFormat)} (${p.percent}%)`
                },

                data: data.map(row => {
                    const extras = {};
                    if (dataCfg.tooltipFields) dataCfg.tooltipFields.forEach(f => extras[f] = row[f]);
                    return {
                        name: (dateFormat !== 'raw') ? formatDate(row.x_val, dateFormat, dateSep, datePat) : row.x_val,
                        value: row[s.id],
                        extras
                    };
                })
            }];
            if (isHalf && type === 'donut') {
                // For true half donut effect, we often need to rotate or adjust.
                // Simple approach: startAngle 180.
            }
        }
    }

    // --- 5. NEW TYPES (Snippet Integration) ---

    // CHORD (Graph Circular)
    else if (type === 'chord') {
        option.series = [{
            type: 'graph',
            layout: 'circular',
            data: dataCfg.nodes?.map(n => ({ ...n, itemStyle: { color: n.color } })),
            links: dataCfg.links,
            roam: true,
            label: { show: true, position: 'right' },
            lineStyle: { color: 'source', curveness: 0.3 }
        }];
    }

    // PARALLEL
    else if (type === 'parallelCoords') {
        option.parallelAxis = dataCfg.dimensions?.map((d, i) => ({ dim: i, name: d }));
        option.parallel = {};
        option.series = [{ type: 'parallel', data: dataCfg.values }];
    }

    // GEO MAP (`map`): USA → centroid bubbles on `geo`; world / fallbacks → same as choropleth.
    // CHOROPLETH (`choropleth`): filled regions + continuous color by measure.
    else if (type === 'map' || type === 'choropleth') {
        const meas = (dataCfg.ySeries || []).find(
            (s) => !s.role || s.role === 'yAxis'
        ) || dataCfg.ySeries?.[0];
        const valId = meas?.id;
        const registeredMapId = inferRegisteredMapId(data, dataCfg.geoMap, dataCfg.geoDimensionHint);

        const merged = new Map();
        data.forEach((r) => {
            if (r.x_val == null || String(r.x_val).trim() === '') return;
            const featureName = choroplethRegionFeatureName(r.x_val, registeredMapId);
            if (!featureName) return;
            const v = valId != null ? r[valId] : undefined;
            const num = Number(v);
            const value = Number.isFinite(num) ? num : 0;
            merged.set(featureName, (merged.get(featureName) || 0) + value);
        });
        const seriesData = Array.from(merged.entries()).map(([name, value]) => ({
            name,
            value,
        }));

        const nums = seriesData.map((d) => d.value).filter(Number.isFinite);
        const vmin = nums.length ? Math.min(0, ...nums) : 0;
        const vmax = nums.length ? Math.max(1, ...nums) : 1;

        option.tooltip = { ...(option.tooltip || {}), trigger: 'item' };

        const mergedMapStyle = { ...(CHART_DEFAULTS.map || {}), ...(style.map || {}) };
        const mapRoam = mergedMapStyle.roam !== undefined ? mergedMapStyle.roam : true;
        const scaleLimitResolved = resolveMapSeriesScaleLimit(mergedMapStyle);
        const inRangeColors = style.colors?.length ? style.colors : ['#dbeafe', '#1d4ed8'];

        const useChoroplethStyle = type === 'choropleth' || registeredMapId !== 'USA';

        const choroplethSeries = () => ({
            type: 'map',
            map: registeredMapId,
            roam: mapRoam,
            scaleLimit: scaleLimitResolved,
            emphasis: { label: { show: true }, itemStyle: { areaColor: '#93c5fd' } },
            data: seriesData,
        });

        if (useChoroplethStyle) {
            option.visualMap = {
                min: vmin,
                max: vmax,
                left: 'right',
                calculable: true,
                inRange: { color: inRangeColors },
            };
            option.series = [choroplethSeries()];
        } else {
            const scatterData = [];
            seriesData.forEach(({ name: regionName, value: metric }) => {
                const center = usaFeatureCenterLngLat(regionName);
                if (!center) return;
                scatterData.push({
                    name: regionName,
                    value: [...center, Number(metric) || 0],
                });
            });

            const bubbleSymbolSize = (dataValue) => {
                const numeric = Number(Array.isArray(dataValue) ? dataValue[2] : dataValue) || 0;
                let t = vmin < vmax ? (numeric - vmin) / (vmax - vmin) : 1;
                t = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0;
                return Math.round(8 + t * 22);
            };

            if (scatterData.length === 0) {
                option.visualMap = {
                    min: vmin,
                    max: vmax,
                    left: 'right',
                    calculable: true,
                    inRange: { color: inRangeColors },
                };
                option.series = [choroplethSeries()];
            } else {
                option.geo = {
                    map: 'USA',
                    roam: mapRoam,
                    scaleLimit: scaleLimitResolved,
                    silent: true,
                    emphasis: { disabled: true },
                    label: { show: false },
                    itemStyle: {
                        areaColor: '#f8fafc',
                        borderColor: '#94a3b8',
                        borderWidth: 0.4,
                    },
                };
                option.visualMap = {
                    min: vmin,
                    max: vmax,
                    left: 'right',
                    calculable: true,
                    dimension: 2,
                    inRange: { color: inRangeColors },
                };
                option.series = [{
                    type: 'scatter',
                    coordinateSystem: 'geo',
                    geoIndex: 0,
                    name: meas?.name || meas?.id || '',
                    tooltip: {},
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 11,
                            color: '#0f172a',
                            formatter: (p) => (p?.name != null ? String(p.name) : ''),
                        },
                        itemStyle: {
                            shadowBlur: 8,
                            shadowColor: 'rgba(15, 23, 42, 0.2)',
                        },
                    },
                    symbolSize(val) {
                        return bubbleSymbolSize(val);
                    },
                    itemStyle: {
                        opacity: 0.93,
                        borderColor: '#0f172a2e',
                        borderWidth: 0.5,
                    },
                    z: 3,
                    data: scatterData,
                }];
            }
        }
    }

    // --- 7. SANKEY ---
    else if (type === 'sankey') {
        // Transform Source (x_val) -> Target (group_val) -> Flow (ySeries[0])
        const valId = dataCfg.ySeries[0]?.id;
        const links = data.map(r => ({
            source: r.x_val,
            target: r.group_val || r.y_val, // Fallback if group_val missing
            value: Number(r[valId]) || 0
        }));
        // Deduplicate nodes
        const nodes = Array.from(new Set(links.flatMap(l => [l.source, l.target])))
            .filter(n => n)
            .map(n => ({ name: n }));

        option.tooltip = { trigger: 'item', triggerOn: 'mousemove' };
        option.series = [{
            type: 'sankey',
            layout: 'none',
            orient: style.sankey?.orient || 'horizontal',
            data: nodes,
            links: links,
            emphasis: { focus: 'adjacency' },
            itemStyle: { borderWidth: 1, borderColor: '#aaa' },
            lineStyle: { color: 'source', curveness: 0.5 },
            label: { position: style.sankey?.align === 'left' ? 'left' : style.sankey?.align === 'right' ? 'right' : 'top' }
        }];
    }

    // --- 8. FUNNEL ---
    else if (type === 'funnel') {
        const valId = dataCfg.ySeries[0]?.id;
        option.tooltip = { trigger: 'item' };
        option.series = [{
            type: 'funnel',
            left: '10%', top: 60, bottom: 60, width: '80%',
            sort: 'descending',
            gap: 2,
            label: { show: true, position: 'inside' },
            data: data.map(r => ({
                name: r.x_val,
                value: Number(r[valId]) || 0
            }))
        }];
    }

    // --- 9. TREEMAP ---
    else if (type === 'treemap') {
        // ECharts treemap `value` must be a single number (tile area). Using [size, color]
        // produced bogus tooltips ("series0: 119.84,0") and wrong series labels.
        const sizeSeries =
            dataCfg.ySeries?.find((s) => s.role === 'yAxis' || !s.role) ||
            dataCfg.ySeries?.[0];
        const colorSeries = dataCfg.ySeries?.find((s) => s.role === 'treemapColor');
        const valId = sizeSeries?.id;
        const colorId = colorSeries?.id;

        const readSize = (r) => Number(valId != null ? r[valId] : 0) || 0;
        const readColor = (r) => (colorId != null ? Number(r[colorId]) || 0 : null);

        const seriesName = sizeSeries?.name || sizeSeries?.id || 'Value';
        const c1 = style.colors?.[0] || '#3b82f6';
        const c2 = style.colors?.[1] || '#8b5cf6';

        let treemapData;
        if (colorId) {
            const cVals = data.map(readColor).filter((n) => n !== null && Number.isFinite(n));
            const cMin = cVals.length ? Math.min(...cVals) : 0;
            const cMax = cVals.length ? Math.max(...cVals) : 1;
            treemapData = data.map((r) => {
                const size = readSize(r);
                const cVal = readColor(r) ?? 0;
                const tNorm = cMax !== cMin ? (cVal - cMin) / (cMax - cMin) : 0.5;
                const node = {
                    name: r.x_val,
                    value: size,
                    itemStyle: { color: lerpHexColor(c1, c2, tNorm) },
                    [colorId]: cVal
                };
                return node;
            });
        } else {
            treemapData = data.map((r) => ({
                name: r.x_val,
                value: readSize(r),
                itemStyle: { color: c1 }
            }));
        }

        option.series = [{
            name: seriesName,
            type: 'treemap',
            leafDepth: style.treemap?.leafDepth || 1,
            data: treemapData,
            levels: [
                { itemStyle: { borderColor: '#555', borderWidth: 4, gapWidth: 4 } },
                {
                    ...(colorId ? {} : { colorSaturation: [0.3, 0.6] }),
                    itemStyle: { borderColorSaturation: 0.7, gapWidth: 2, borderWidth: 2 }
                }
            ]
        }];
    }

    // --- 10. GAUGE / KPI ---
    // --- 10. KPI (Big Number + Trend) ---
    else if (type === 'kpi') {
        try {
            const valSeries = dataCfg.ySeries.find(s => !s.role || s.role === 'yAxis') || dataCfg.ySeries[0];
            const targetSeries = dataCfg.ySeries.find(s => s.role === 'target');
            const compareSeries = dataCfg.ySeries.find(s => s.role === 'comparison');

            const row = data[0] || {};
            // console.log("[chartOptionsBuilder] KPI Data Context:", { valSeries, targetSeries, compareSeries, row });
            const value = valSeries ? Number(row[valSeries.id]) || 0 : 0;
            const target = targetSeries ? (Number(row[targetSeries.id]) || 0) : null;
            const compare = compareSeries ? (Number(row[compareSeries.id]) || 0) : null;

            // Trend Logic
            let trend = 'neutral'; // neutral, up, down
            let diff = 0;
            let diffPct = 0;
            const baseline = target !== null ? target : compare;

            if (baseline !== null && baseline !== 0) {
                diff = value - baseline;
                diffPct = (diff / Math.abs(baseline)) * 100;
                trend = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';
            }

            // Color Logic
            const trendMode = style?.kpi?.trendColor || 'normal';
            const colors = {
                up: trendMode === 'inverse' ? '#ef4444' : (trendMode === 'neutral' ? '#3b82f6' : '#22c55e'),
                down: trendMode === 'inverse' ? '#22c55e' : (trendMode === 'neutral' ? '#3b82f6' : '#ef4444'),
                neutral: '#64748b'
            };
            const trendColor = trend === 'up' ? colors.up : trend === 'down' ? colors.down : colors.neutral;
            const trendIcon = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '';

            // Formatting
            const formattedValue = formatValue(value, globalLabelFormat);
            const formattedDiff = `${diff > 0 ? '+' : ''}${formatValue(diff, 'compact')} (${diff > 0 ? '+' : ''}${diffPct.toFixed(1)}%)`;

            // Alignment Layout
            const align = style?.kpi?.align || 'center';
            const xPos = align === 'left' ? '10%' : align === 'right' ? '90%' : '50%';
            const textAlign = align;

            option.series = []; // No chart series, just graphics
            option.graphic = [
                {
                    type: 'text',
                    left: xPos,
                    top: '40%',
                    style: {
                        text: formattedValue,
                        textAlign: textAlign,
                        fill: style?.colors?.[0] || '#1e293b',
                        fontSize: style?.kpi?.fontSize || 60,
                        fontWeight: 'bold'
                    }
                },
                {
                    type: 'text',
                    left: xPos,
                    top: '65%',
                    style: {
                        text: valSeries?.label || 'KPI Metric',
                        textAlign: textAlign,
                        fill: '#64748b',
                        fontSize: 14
                    }
                }
            ];

            // Append Trend Graphic if enabled
            if (style?.kpi?.showTrend !== false && baseline !== null) {
                option.graphic.push({
                    type: 'text',
                    left: xPos,
                    top: '25%', // Top of the Value
                    style: {
                        text: `${trendIcon} ${formattedDiff}`,
                        textAlign: textAlign,
                        fill: trendColor,
                        fontSize: 14,
                        fontWeight: 'bold'
                    }
                });
            }
        } catch (err) {
            console.error("KPI Render Error:", err);
            option.graphic = [{
                type: 'text',
                left: 'center',
                top: 'center',
                style: {
                    text: `Error: ${err.message}`, // Show error on canvas
                    fill: 'red',
                    fontSize: 12
                }
            }];
        }
    }

    // --- 11. GAUGE (Standard) ---
    else if (type === 'gauge') {
        const valSeries = dataCfg.ySeries.find(s => !s.role || s.role === 'yAxis') || dataCfg.ySeries[0];
        const minSeries = dataCfg.ySeries.find(s => s.role === 'min');
        const maxSeries = dataCfg.ySeries.find(s => s.role === 'max');
        const targetSeries = dataCfg.ySeries.find(s => s.role === 'target'); // Fetch Target

        const row = data[0] || {};
        const val = Number(row[valSeries?.id]) || 0;

        // Priority: Style Config -> Data Field -> Default
        const min = style.gauge?.min !== undefined ? Number(style.gauge.min) : (minSeries ? (Number(row[minSeries.id]) || 0) : 0);
        const max = style.gauge?.max !== undefined ? Number(style.gauge.max) : (maxSeries ? (Number(row[maxSeries.id]) || 100) : 100);
        const target = style.gauge?.target !== undefined ? Number(style.gauge.target) : (targetSeries ? (Number(row[targetSeries.id]) || null) : null);

        // Metric Label Priority: Display Name Override -> Field Label -> 'Metric'
        let metricLabel = valSeries?.label || 'Metric';
        if (valSeries && style.seriesFormats?.[valSeries.id]?.displayName) {
            metricLabel = style.seriesFormats[valSeries.id].displayName;
        }

        const gaugeData = [{ value: val, name: metricLabel }];

        // Add Target Pointer if present
        if (target !== null) {
            gaugeData.push({
                value: target,
                name: style.gauge?.targetLabel || 'Target', // Use override or default
                itemStyle: { color: '#ef4444' }, // Red target marker
                pointer: { width: 3, length: '70%', offsetCenter: [0, '-10%'] }, // Distinctive pointer
                title: { offsetCenter: [0, '-40%'], fontSize: 10, color: '#ef4444' }
            });
        }

        option.series = [{
            type: 'gauge',
            min: min,
            max: max,
            progress: { show: !!style.gauge?.progressBar, width: style.gauge?.axisWidth || 10 },
            axisLine: { lineStyle: { width: style.gauge?.axisWidth || 10 } },
            pointer: { width: 6 }, // Default pointer
            // Metric Name Positioning: Moved to top (-60%) to avoid overlap with Value (70%)
            title: {
                show: true,
                offsetCenter: [0, '-60%'],
                fontSize: 12,
                color: '#64748b'
            },
            axisLabel: {
                formatter: (val) => {
                    if (valSeries && style.seriesFormats?.[valSeries.id]) {
                        return formatValue(val, style.seriesFormats[valSeries.id]);
                    }
                    return formatValue(val, 'compact');
                },
                fontSize: 10
            },
            detail: {
                valueAnimation: true,
                formatter: (val) => {
                    if (valSeries && style.seriesFormats?.[valSeries.id]) {
                        return formatValue(val, style.seriesFormats[valSeries.id]);
                    }
                    // Fallback for manual/unmapped data
                    return formatValue(val, globalLabelFormat || 'compact');
                },
                offsetCenter: [0, '70%'],
                fontSize: 16
            },
            data: gaugeData
        }];
    }

    // --- 6. SCATTER / BUBBLE (Value vs Value) ---
    else if (['scatter', 'bubble'].includes(type)) {
        // X-Axis = Value, Y-Axis = Value
        option.xAxis = {
            type: 'value',
            name: (style.xAxis?.showTitle === true) ? style.xAxis?.titleText : dataCfg.ySeries[0]?.label, // Default to X-Measure Name
            nameLocation: 'middle',
            nameGap: 30,
            splitLine: { show: style.xAxis?.showGrid !== false },
            axisLabel: { formatter: (v) => formatValue(v, 'compact') }
        };
        option.yAxis = {
            type: 'value',
            name: (style.yAxis?.showTitle === true) ? style.yAxis?.titleText : dataCfg.ySeries[1]?.label, // Default to Y-Measure Name
            nameLocation: 'middle',
            nameGap: 30,
            splitLine: { show: style.yAxis?.showGrid !== false },
            axisLabel: { formatter: (v) => formatValue(v, 'compact') }
        };

        const xId = dataCfg.ySeries[0]?.id;
        const yId = dataCfg.ySeries[1]?.id;
        const sizeId = dataCfg.ySeries[2]?.id; // Bubble Size

        option.series = [{
            type: 'scatter',
            symbolSize: (val) => {
                if (type === 'bubble' && sizeId) {
                    // Normalize size (Simple scaling)
                    // In real app, compute min/max of sizeId column first
                    const sizeVal = val[2];
                    return Math.max(5, Math.min(80, (sizeVal / 100) * 50)); // Placeholder scaling
                }
                return style.line?.symbolSize || 10;
            },
            data: data.map(row => {
                // [X, Y, Size, Label]
                const x = Number(row[xId]) || 0;
                const y = Number(row[yId]) || 0;
                const size = sizeId ? (Number(row[sizeId]) || 0) : 1;
                return [x, y, size, row.x_val || '']; // row.x_val is the Legend/Dimension (e.g. Customer)
            }),
            itemStyle: {
                color: style.colors?.[0] || '#5470c6',
                opacity: 0.7
            },
            tooltip: {
                formatter: (p) => {
                    const label = p.value[3];
                    const xV = formatValue(p.value[0]);
                    const yV = formatValue(p.value[1]);
                    return `<b>${label}</b><br/>X: ${xV}<br/>Y: ${yV}`;
                }
            }
        }];
    }

    // Preserve existing Heatmap/Gauge/Funnel/Radar from previous code?
    // Re-implementing simplified based on "Hybrid" goal (Using ECharts defaults + Formatters)

    else if (type === 'heatmap') {
        // groupField holds the Y dimension column name (e.g. "dbo_FactCharge.DateOfService")
        // It comes from builderConfig.data.groupField = config.yAxis[0].name
        const xField = 'x_val';
        const yField = dataCfg.groupField; // The actual key in the data row for Y categories
        const valId = dataCfg.ySeries?.[0]?.id; // The measure field key in the data row

        // Build unique sorted categories
        const xCats = [...new Set(data.map(r => r[xField]).filter(v => v !== null && v !== undefined))];
        const yCats = [...new Set(data.map(r => r[yField]).filter(v => v !== null && v !== undefined))];

        // Build heatmap data points as [xIdx, yIdx, value]
        const hmData = [];
        data.forEach(r => {
            const xIdx = xCats.indexOf(r[xField]);
            const yIdx = yCats.indexOf(r[yField]);
            if (xIdx !== -1 && yIdx !== -1) {
                hmData.push([xIdx, yIdx, Number(r[valId]) || 0]);
            }
        });

        // Compute visualMap range from actual data
        const allVals = hmData.map(d => d[2]);
        const minVal = allVals.length > 0 ? Math.min(...allVals) : 0;
        const maxVal = allVals.length > 0 ? Math.max(...allVals) : 100;

        // Set tooltip trigger to 'item' for heatmap
        option.tooltip.trigger = 'item';

        option.xAxis = { type: 'category', data: xCats, splitArea: { show: true } };
        option.yAxis = { type: 'category', data: yCats, splitArea: { show: true } };
        option.visualMap = {
            min: minVal,
            max: maxVal,
            calculable: true,
            orient: 'horizontal',
            bottom: 0,
            left: 'center',
            inRange: { color: style.heatmap?.colors || ['#f6efa6', '#d88273', '#bf444c'] }
        };

        option.series = [{
            type: 'heatmap',
            data: hmData,
            label: { show: style.showDataLabel }
        }];
    }

    // ... Other existing types (Radar, Funnel Gauge) fallback to default ECharts behavior mostly ...

    return option;
};